import React, { FC, useEffect, useRef, useState } from 'react';
import { iconSize } from '../../../constants/icon.constants';
import { getUTCDate } from '../../../hooks/useFormatDate';
import { getCanUseDate, getEndOfDate, getTimeline } from '../../../utils/eventsListings.utils';
import { documentService } from '../../../utils/window.utils';
import { ctaTypes } from '../cta';
import DictionaryTerm from '../dictionaryTerm';
import Icon from '../icon';
import RoutableNotEditableLink from '../routableNotEditableLink';
import { TCalenderEvent, TSaveCalendarEventButtonType, TSaveCalendarEventProps } from './types/saveEvent.types';

const SaveEventOptionWrapper: FC<Partial<TCalenderEvent> & { children: React.ReactNode}> = ({ calendarDownload = false, calendarUrl, children }) => {
  return calendarDownload ? (
    // special case to not use RoutableNotEditableLink
    // ignored to allow download
    <a href={calendarUrl} className="save-event__option-link">
      {children}
    </a>
  ) : (
    <RoutableNotEditableLink href={calendarUrl} target="_blank" className="save-event__option-link">
      {children}
    </RoutableNotEditableLink>
  );
};

const SaveCalendarEvent: FC<Partial<TSaveCalendarEventProps>> = ({
  calendarDetails = [],
  buttonType = TSaveCalendarEventButtonType.SECONDARYBLACK,
  startDate,
  endDate,
  extraClassName = '',
}) => {
  const canUseStartDate = getCanUseDate(startDate);
  const canUseEndDate = getCanUseDate(endDate);
  const eventTimeline =
    startDate && canUseStartDate
      ? getTimeline(getUTCDate(startDate), endDate && canUseEndDate ? getUTCDate(endDate) : getEndOfDate(startDate))
      : '';
  const [shouldShowDropdownList, setShouldShowDropdownList] = useState(false);
  const dropdownElmRef = useRef<HTMLButtonElement>(null);
  const baseButtonClass = 'save-event__button';
  const buttonTypeClassMap = {
    [TSaveCalendarEventButtonType['SECONDARYBLACK']]: baseButtonClass,
    [TSaveCalendarEventButtonType['SMALL']]: `${baseButtonClass}--${buttonType}`,
    [TSaveCalendarEventButtonType[
      'PRIMARY'
    ]]: `${ctaTypes['secondaryBaseButton']} ${ctaTypes['secondaryBaseButton']}--${ctaTypes.primaryRed} ${baseButtonClass}--${buttonType}`,
    [TSaveCalendarEventButtonType[
      'PRIMARYWHITE'
    ]]: `${baseButtonClass}--${ctaTypes.primaryWhite}`,
  };

  const handleClickOutside = (e) => {
    if (dropdownElmRef?.current?.contains(e?.target)) return;
    setShouldShowDropdownList(false);
  };

  useEffect(() => {
    const doc = documentService() as Document;
    doc.addEventListener('click', handleClickOutside, false);

    return () => doc.removeEventListener('click', handleClickOutside, false);
  }, [calendarDetails]);

  if (!calendarDetails?.length || !eventTimeline || eventTimeline === 'PAST') return null;

  return (
    <div className={`save-event${extraClassName}`}>
      <button
        className={buttonTypeClassMap[buttonType]}
        onClick={() => setShouldShowDropdownList(!shouldShowDropdownList)}
        ref={dropdownElmRef}
      >
        <p className="save-event__title">
          <DictionaryTerm keyword="cnhi-save-event" />
        </p>
        <Icon iconName={'time-calendar'} size={iconSize.dsk} color={'primary'} extraClassName={`${baseButtonClass}-icon`} />
      </button>
      {shouldShowDropdownList && (
        <ul className="save-event__options">
          {calendarDetails.map(({ calendarDownload, calendarUrl, calendarIcon, calendarTitle }, index) => {
            return (
              // eslint-disable-next-line react/jsx-key
              <li key={`${calendarTitle}-${index}`} className="save-event__option">
                <SaveEventOptionWrapper calendarDownload={calendarDownload} calendarUrl={calendarUrl}>
                  <Icon iconField={calendarIcon} size={iconSize.dsk} extraClassName="save-event__option-icon" />
                  <p className="save-event__option-title"><DictionaryTerm keyword={calendarTitle}/></p>
                </SaveEventOptionWrapper>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default SaveCalendarEvent;
